import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Calendar as CalendarIcon, Clock, ChevronLeft, ChevronRight, Check, Globe, ArrowLeft, Video } from "lucide-react";
import { Button, Input, Label, Skeleton, Textarea } from "@/components/ui";
import { formatDate, formatTime, formatLocalDateKey } from "@/lib/format";
import { cn } from "@/lib/utils";

interface PublicCalendar {
  id: string; name: string; slug: string; description: string | null;
  duration_minutes: number; timezone: string; color: string;
  settings: Record<string, unknown>;
  /** The booking comes with a Google Meet / Teams link (calendar is linked to one). */
  video_call?: boolean;
}
interface Slot { start: string; end: string }

type Step = "date" | "time" | "details" | "done";

/**
 * Calendly-style public booking page. Three steps:
 *   1. Pick a date from a month grid
 *   2. Pick a time slot for that date
 *   3. Enter your details + confirm
 *
 * Mounted at /book/:slug as a public, no-auth route.
 */
export function BookingPage() {
  const { slug } = useParams<{ slug: string }>();
  const [calendar, setCalendar] = useState<PublicCalendar | null>(null);
  const [calendarError, setCalendarError] = useState<string | null>(null);
  const [calendarLoading, setCalendarLoading] = useState(true);

  const [step, setStep] = useState<Step>("date");
  const [cursor, setCursor] = useState(() => new Date());
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsByDate, setSlotsByDate] = useState<Record<string, boolean>>({});
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [bookError, setBookError] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<{ starts_at: string; ends_at: string; meeting_url: string | null } | null>(null);

  // Load calendar metadata.
  useEffect(() => {
    if (!slug) return;
    setCalendarLoading(true);
    fetch(`/api/public/calendars/${slug}`)
      .then(async (r) => {
        if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || "Calendar not found");
        return r.json();
      })
      .then((d) => setCalendar(d.calendar))
      .catch((e) => setCalendarError(e instanceof Error ? e.message : "Failed"))
      .finally(() => setCalendarLoading(false));
  }, [slug]);

  // Pre-fetch availability for the visible month so we can dim days with no slots.
  useEffect(() => {
    if (!slug || !calendar) return;
    const days = monthDays(cursor);
    const today = new Date(); today.setHours(0,0,0,0);
    const future = days.filter((d) => d.isCurrentMonth && d.date >= today).map((d) => isoDate(d.date));
    let cancelled = false;
    Promise.all(future.map(async (date) => {
      try {
        const r = await fetch(`/api/public/calendars/${slug}/slots?date=${date}`);
        if (!r.ok) return [date, false] as const;
        const d = await r.json() as { slots: Slot[] };
        return [date, (d.slots ?? []).length > 0] as const;
      } catch { return [date, false] as const; }
    })).then((pairs) => {
      if (cancelled) return;
      const map: Record<string, boolean> = {};
      for (const [d, ok] of pairs) map[d] = ok;
      setSlotsByDate(map);
    });
    return () => { cancelled = true; };
  }, [slug, calendar, cursor]);

  // Fetch slots when a date is selected.
  useEffect(() => {
    if (!slug || !selectedDate) return;
    setSlotsLoading(true);
    fetch(`/api/public/calendars/${slug}/slots?date=${selectedDate}`)
      .then((r) => r.ok ? r.json() : { slots: [] })
      .then((d) => setSlots(d.slots ?? []))
      .catch(() => setSlots([]))
      .finally(() => setSlotsLoading(false));
  }, [slug, selectedDate]);

  const submit = async () => {
    if (!slug || !selectedSlot || !name.trim() || (!email && !phone)) return;
    setSubmitting(true);
    setBookError(null);
    try {
      const r = await fetch(`/api/public/calendars/${slug}/book`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          starts_at: selectedSlot.start,
          ends_at:   selectedSlot.end,
          name, email: email || undefined, phone: phone || undefined, notes,
        }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || "Booking failed");
      setConfirmation({ starts_at: data.starts_at, ends_at: data.ends_at, meeting_url: typeof data.meeting_url === "string" ? data.meeting_url : null });
      setStep("done");
    } catch (e) {
      setBookError(e instanceof Error ? e.message : "Failed");
    } finally { setSubmitting(false); }
  };

  if (calendarLoading) {
    return <div className="mx-auto max-w-3xl p-6"><Skeleton className="h-96" /></div>;
  }

  if (calendarError || !calendar) {
    return (
      <div className="mx-auto max-w-md p-8 text-center">
        <h1 className="text-lg font-semibold text-zinc-200">Calendar not found</h1>
        <p className="mt-2 text-sm text-zinc-500">{calendarError ?? "This booking link doesn't exist or has been disabled."}</p>
      </div>
    );
  }

  if (step === "done" && confirmation) {
    return (
      <div className="min-h-screen bg-zinc-950 px-4 py-12 sm:px-6 flex items-center justify-center">
        <div className="max-w-md w-full text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/20 ring-4 ring-emerald-500/10">
            <Check className="h-7 w-7 text-emerald-400" />
          </div>
          <h1 className="mt-5 text-2xl font-semibold text-zinc-100">You&rsquo;re booked!</h1>
          <p className="mt-3 text-base text-zinc-300">
            {formatDate(confirmation.starts_at, { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
          </p>
          <p className="mt-1 text-base text-zinc-400">
            {formatTime(confirmation.starts_at)} to {formatTime(confirmation.ends_at)}
          </p>
          <p className="mt-1 text-xs text-zinc-500 inline-flex items-center gap-1">
            <Globe className="h-3 w-3" />
            {calendar.timezone}
          </p>
          {confirmation.meeting_url && (
            <div className="mt-6">
              <a
                href={confirmation.meeting_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500"
              >
                <Video className="h-4 w-4" />
                Join video call
              </a>
              <p className="mt-2 text-xs text-zinc-500">Save this link - it is also in your calendar invite.</p>
            </div>
          )}
          <p className="mt-8 text-xs text-zinc-500">A confirmation will be sent to {email || phone}.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 px-4 py-8 sm:px-6 sm:py-12">
      <div className="mx-auto max-w-5xl rounded-2xl border border-zinc-800/70 bg-zinc-900/30 shadow-2xl shadow-shade/30 overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-[20rem_1fr]">
          {/* Sidebar */}
          <aside className="border-b border-zinc-800/70 bg-zinc-950 p-6 lg:border-b-0 lg:border-r lg:p-8">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-indigo-500/20">
              <CalendarIcon className="h-5 w-5 text-indigo-300" />
            </div>
            <h1 className="mt-5 text-xl font-semibold text-zinc-100">{calendar.name}</h1>
            <div className="mt-3 space-y-1.5 text-sm text-zinc-400">
              <p className="flex items-center gap-2">
                <Clock className="h-3.5 w-3.5 text-zinc-500" />
                {calendar.duration_minutes} minutes
              </p>
              <p className="flex items-center gap-2">
                <Globe className="h-3.5 w-3.5 text-zinc-500" />
                {calendar.timezone}
              </p>
            </div>
            {calendar.description && <p className="mt-5 text-sm text-zinc-400 leading-relaxed">{calendar.description}</p>}

            {step !== "date" && (
              <button
                type="button"
                onClick={() => { if (step === "time") setStep("date"); else setStep("time"); }}
                className="mt-6 inline-flex items-center gap-1.5 rounded-md text-xs text-zinc-400 hover:text-zinc-200"
              >
                <ArrowLeft className="h-3 w-3" /> Change {step === "details" ? "time" : "date"}
              </button>
            )}
          </aside>

          {/* Step content */}
          <div className="p-6 lg:p-8">
            {step === "date" && (
              <DateStep
                cursor={cursor}
                onCursor={setCursor}
                slotsByDate={slotsByDate}
                onPick={(date) => { setSelectedDate(date); setStep("time"); setSelectedSlot(null); }}
              />
            )}
            {step === "time" && selectedDate && (
              <TimeStep
                date={selectedDate}
                slots={slots}
                loading={slotsLoading}
                selected={selectedSlot}
                onPick={(s) => { setSelectedSlot(s); setStep("details"); }}
              />
            )}
            {step === "details" && selectedSlot && (
              <DetailsStep
                slot={selectedSlot}
                name={name} setName={setName}
                email={email} setEmail={setEmail}
                phone={phone} setPhone={setPhone}
                notes={notes} setNotes={setNotes}
                error={bookError}
                submitting={submitting}
                onSubmit={submit}
                videoCall={calendar.video_call === true}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Step 1: pick a date ───────────────────────────────────────────────────
function DateStep({
  cursor, onCursor, slotsByDate, onPick,
}: {
  cursor: Date;
  onCursor: (d: Date) => void;
  slotsByDate: Record<string, boolean>;
  onPick: (date: string) => void;
}) {
  const days = useMemo(() => monthDays(cursor), [cursor]);
  const today = useMemo(() => { const d = new Date(); d.setHours(0,0,0,0); return d; }, []);
  return (
    <div>
      <div className="mb-5 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-zinc-100">Select a date &amp; time</h2>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
            aria-label="Previous month"
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-zinc-400 hover:bg-zinc-800/60 hover:text-zinc-100"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="min-w-32 text-center text-base font-medium text-zinc-200">
            {cursor.toLocaleDateString(undefined, { month: "long", year: "numeric" })}
          </span>
          <button
            type="button"
            onClick={() => onCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
            aria-label="Next month"
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-zinc-400 hover:bg-zinc-800/60 hover:text-zinc-100"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 mb-2">
        {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => (
          <div key={d} className="py-2 text-center text-xs font-semibold uppercase tracking-wider text-zinc-500">
            {d.slice(0, 3)}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1 place-items-center">
        {days.map(({ date, isCurrentMonth }) => {
          const iso = isoDate(date);
          const isPast = date < today;
          const isToday = iso === isoDate(today);
          const hasSlots = slotsByDate[iso];
          // While availability is loading we don't know yet → enable optimistically.
          const disabled = !isCurrentMonth || isPast || hasSlots === false;
          return (
            <button
              key={iso}
              type="button"
              disabled={disabled}
              onClick={() => onPick(iso)}
              className={cn(
                "relative h-9 w-9 sm:h-10 sm:w-10 rounded-full text-sm font-medium transition-all flex items-center justify-center",
                disabled && "text-zinc-700",
                !disabled && "text-indigo-200 bg-indigo-500/10 hover:bg-indigo-500 hover:text-white hover:shadow-md hover:shadow-indigo-500/20 cursor-pointer",
                isToday && !disabled && "ring-1 ring-indigo-400/60",
              )}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>

      <p className="mt-5 text-xs text-zinc-500 flex items-center gap-1.5">
        <span className="inline-block h-1.5 w-1.5 rounded-full bg-indigo-400" />
        Available days are highlighted
      </p>
    </div>
  );
}

// ─── Step 2: pick a time ───────────────────────────────────────────────────
function TimeStep({
  date, slots, loading, selected, onPick,
}: {
  date: string;
  slots: Slot[];
  loading: boolean;
  selected: Slot | null;
  onPick: (s: Slot) => void;
}) {
  return (
    <div>
      <h2 className="text-base font-semibold text-zinc-100">
        {formatDate(date + "T00:00:00", { weekday: "long", month: "long", day: "numeric" })}
      </h2>
      <p className="mt-0.5 text-xs text-zinc-500">Select a time</p>

      <div className="mt-4">
        {loading ? (
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {Array.from({length: 6}).map((_, i) => <Skeleton key={i} className="h-10" />)}
          </div>
        ) : slots.length === 0 ? (
          <p className="py-12 text-center text-sm text-zinc-500">No times available on this day.</p>
        ) : (
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 max-h-96 overflow-y-auto">
            {slots.map((s) => (
              <button
                key={s.start}
                type="button"
                onClick={() => onPick(s)}
                className={cn(
                  "rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors",
                  selected?.start === s.start
                    ? "border-indigo-500 bg-indigo-500/15 text-indigo-100"
                    : "border-zinc-800 bg-zinc-900/30 text-zinc-300 hover:border-indigo-500/50 hover:bg-zinc-900"
                )}
              >
                {formatTime(s.start)}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Step 3: details ───────────────────────────────────────────────────────
function DetailsStep({
  slot, name, setName, email, setEmail, phone, setPhone, notes, setNotes,
  error, submitting, onSubmit, videoCall,
}: {
  slot: Slot;
  name: string; setName: (s: string) => void;
  email: string; setEmail: (s: string) => void;
  phone: string; setPhone: (s: string) => void;
  notes: string; setNotes: (s: string) => void;
  error: string | null;
  submitting: boolean;
  onSubmit: () => void;
  videoCall?: boolean;
}) {
  return (
    <div>
      <h2 className="text-base font-semibold text-zinc-100">Confirm your details</h2>
      <p className="mt-0.5 text-xs text-zinc-500">
        {formatDate(slot.start, { weekday: "long", month: "short", day: "numeric" })} · {formatTime(slot.start)} – {formatTime(slot.end)}
      </p>
      {videoCall && (
        <p className="mt-2 inline-flex items-center gap-1.5 text-xs text-zinc-400">
          <Video className="h-3.5 w-3.5 text-indigo-300" />
          This is a video call - you&rsquo;ll get the link as soon as you book.
        </p>
      )}

      <form
        className="mt-4 space-y-3"
        onSubmit={(e) => { e.preventDefault(); onSubmit(); }}
      >
        <div><Label required>Your name</Label><Input value={name} onChange={(e) => setName(e.target.value)} autoFocus /></div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div><Label>Email</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} /></div>
          <div><Label>Phone</Label><Input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} /></div>
        </div>
        <p className="text-[11px] text-zinc-500">At least one of email or phone is required so we can send your confirmation.</p>
        <div><Label>Anything you&rsquo;d like us to know? (optional)</Label><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. team size, tools you use, what you'd like to discuss" /></div>
        {error && <p className="text-sm text-rose-400">{error}</p>}
        <Button type="submit" loading={submitting} disabled={!name.trim() || (!email && !phone)} size="lg" className="w-full">
          Confirm booking
        </Button>
      </form>
    </div>
  );
}

// ─── Helpers ───────────────────────────────────────────────────────────────
// Local-date YYYY-MM-DD key. We can't use toISOString() here because that
// would key dates against UTC and a 9pm slot in PST would land on the
// wrong day. See lib/format.ts for the helper this delegates to.
const isoDate = formatLocalDateKey;
function monthDays(cursor: Date): { date: Date; isCurrentMonth: boolean }[] {
  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const start = new Date(first);
  start.setDate(start.getDate() - start.getDay());
  const out: { date: Date; isCurrentMonth: boolean }[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    out.push({ date: d, isCurrentMonth: d.getMonth() === cursor.getMonth() });
  }
  return out;
}
